import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Alert, Box, Button, Chip, Divider, Grid, LinearProgress, Paper, Stack, Table, TableBody, TableCell, TableHead,
  TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { api, ApiError } from '../api/client';
import type { ExplorerAddress, ExplorerBlock, ExplorerTx } from '../api/types';
import { AddressWithCopy, PageHeader, shortAddr } from '../components/common';

type Target = { kind: 'tx' | 'address' | 'block'; value: string } | null;

/**
 * 자체 조회 화면 (SFR-006, A-9).
 *
 * 외부 익스플로러 딥링크를 대신하는 조회 경로다. 프라이빗 전환 시 외부 링크는 모두 죽지만
 * 이 화면은 서비스가 붙어 있는 RPC 를 그대로 쓰므로 계속 동작한다.
 * 체인 원본에 **서비스 맥락**(어떤 요청이 이 트랜잭션을 만들었는지, 어떤 이벤트가 나왔는지)을 함께 보여준다.
 */
export function Explorer() {
  const [params] = useSearchParams();
  const [q, setQ] = useState(params.get('q') ?? '');
  const [target, setTarget] = useState<Target>(null);
  const [err, setErr] = useState<string | null>(null);

  const overview = useQuery({
    queryKey: ['explorer-overview'],
    queryFn: () => api.get<{
      // feeModel 은 **객체**다(model·baseFeeZero·eip1559Tooling·note).
      // 문자열로 잘못 선언해 두면 타입 검사를 통과한 채 화면에서 객체를 그대로 렌더하게 되고,
      // React 가 렌더 중 예외를 던져 화면 전체가 빈 페이지가 된다(2026-09-01 실제 발생).
      chain: {
        ok: boolean; chainId?: string; block?: number;
        feeModel?: { model: string; baseFeeZero: boolean; eip1559Tooling: boolean; note?: string };
      };
      indexer: { checkpoints: { contract: string; lastBlock: string; lag: number }[]; eventCount: number };
    }>('/explorer/overview'),
    refetchInterval: 15000,
  });

  const search = async (query: string = q) => {
    setErr(null);
    try {
      const r = await api.get<{ kind: 'tx' | 'address' | 'block'; value: string }>(
        `/explorer/search?q=${encodeURIComponent(query.trim())}`,
      );
      setTarget({ kind: r.kind, value: r.value });
    } catch (e) {
      setTarget(null);
      setErr(e instanceof ApiError ? e.message : '검색 실패');
    }
  };

  // 다른 화면의 트랜잭션 해시 링크(/explorer?q=…)로 들어오면 바로 조회한다
  const initial = params.get('q');
  useEffect(() => {
    if (initial) void search(initial);
  }, [initial]);

  const chain = overview.data?.chain;

  return (
    <Box>
      <PageHeader
        crumb="모니터링 · 조회"
        title="블록체인 조회"
        desc="트랜잭션 해시·지갑 주소·블록 번호로 연결된 체인을 직접 조회합니다."
      />

      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            fullWidth
            size="small"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void search(); }}
            placeholder="0x… (트랜잭션 해시 / 블록 해시 / 지갑 주소) 또는 블록 번호"
          />
          <Button variant="contained" onClick={() => void search()} disabled={!q.trim()}>조회</Button>
        </Stack>
        {err && <Alert severity="warning" sx={{ mt: 2 }}>{err}</Alert>}
      </Paper>

      {!target && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h2" mb={2}>체인 현황</Typography>
          {overview.isLoading && <LinearProgress />}
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}><Stat k="체인 ID" v={chain?.chainId ?? '—'} /></Grid>
            <Grid item xs={6} md={3}><Stat k="최신 블록" v={chain?.block?.toLocaleString() ?? '—'} /></Grid>
            <Grid item xs={6} md={3}>
              <Tooltip title={chain?.feeModel?.note ?? ''}>
                <span><Stat k="수수료 모델" v={chain?.feeModel?.model ?? '—'} /></span>
              </Tooltip>
            </Grid>
            <Grid item xs={6} md={3}><Stat k="적재 이벤트" v={(overview.data?.indexer.eventCount ?? 0).toLocaleString()} /></Grid>
          </Grid>
          <Divider sx={{ my: 3 }} />
          <Typography variant="h3" mb={1}>인덱서 진행도</Typography>
          <Table size="small">
            <TableHead>
              <TableRow><TableCell>컨트랙트</TableCell><TableCell align="right">마지막 처리 블록</TableCell><TableCell align="right">지연</TableCell></TableRow>
            </TableHead>
            <TableBody>
              {(overview.data?.indexer.checkpoints ?? []).map((c) => (
                <TableRow key={c.contract} hover onClick={() => setTarget({ kind: 'address', value: c.contract })} sx={{ cursor: 'pointer' }}>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{shortAddr(c.contract)}</TableCell>
                  <TableCell align="right">{Number(c.lastBlock).toLocaleString()}</TableCell>
                  <TableCell align="right">
                    <Chip size="small" color={c.lag > 100 ? 'warning' : 'success'} label={`${c.lag} 블록`} sx={{ fontWeight: 700 }} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {target?.kind === 'tx' && <TxView hash={target.value} onAddress={(a) => setTarget({ kind: 'address', value: a })} />}
      {target?.kind === 'address' && <AddressView addr={target.value} onTx={(h) => setTarget({ kind: 'tx', value: h })} />}
      {target?.kind === 'block' && <BlockView num={target.value} onTx={(h) => setTarget({ kind: 'tx', value: h })} />}

      {target && <Box mt={2}><Button onClick={() => { setTarget(null); setQ(''); }}>← 체인 현황으로</Button></Box>}
    </Box>
  );
}

function Stat({ k, v }: { k: string; v: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">{k}</Typography>
      <Typography sx={{ fontWeight: 800, fontSize: 20 }}>{v}</Typography>
    </Box>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <Stack direction="row" spacing={2} sx={{ py: 0.75, borderBottom: '1px solid #eef1f6' }}>
      <Typography variant="body2" sx={{ width: 160, color: 'text.secondary', flexShrink: 0 }}>{k}</Typography>
      <Box sx={{ fontSize: 14, wordBreak: 'break-all' }}>{v}</Box>
    </Stack>
  );
}

function TxView({ hash, onAddress }: { hash: string; onAddress: (a: string) => void }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['explorer-tx', hash],
    queryFn: () => api.get<ExplorerTx>(`/explorer/tx/${hash}`),
  });
  if (isLoading) return <LinearProgress />;
  if (error) return <Alert severity="warning">{error instanceof ApiError ? error.message : '조회 실패'}</Alert>;
  if (!data) return null;

  return (
    <Stack spacing={3}>
      <Paper sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" spacing={2} mb={2}>
          <Typography variant="h2">트랜잭션</Typography>
          <Chip
            size="small"
            color={data.status === 'SUCCESS' ? 'success' : data.status === 'FAILED' ? 'error' : 'warning'}
            label={data.status === 'SUCCESS' ? '성공' : data.status === 'FAILED' ? '실패' : '대기'}
            sx={{ fontWeight: 700 }}
          />
          {data.type === 22 && <Chip size="small" color="info" label="수수료 대납(type-22)" sx={{ fontWeight: 700 }} />}
        </Stack>
        <Row k="해시" v={<code>{data.hash}</code>} />
        <Row k="블록" v={data.blockNumber?.toLocaleString() ?? '—'} />
        <Row k="시각" v={data.timestampIso ? new Date(data.timestampIso).toLocaleString('ko-KR') : '—'} />
        <Row k="보낸 주소" v={data.from ? <LinkAddr addr={data.from} onClick={onAddress} /> : '—'} />
        <Row k="받는 주소" v={data.to ? <LinkAddr addr={data.to} onClick={onAddress} /> : <i>컨트랙트 배포</i>} />
        {data.contractAddress && <Row k="배포된 컨트랙트" v={<LinkAddr addr={data.contractAddress} onClick={onAddress} />} />}
        <Row k="값" v={`${data.value ?? '0'} META`} />
        <Row k="논스" v={String(data.nonce ?? '—')} />
        <Row k="가스 사용" v={data.gasUsed ? Number(data.gasUsed).toLocaleString() : '—'} />
        <Row k="수수료" v={data.feePaid ? `${data.feePaid} META` : '—'} />
        <Row k="입력 크기" v={data.inputSize !== null ? `${data.inputSize} bytes` : '—'} />
      </Paper>

      {data.request && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h2" mb={2}>서비스 요청 맥락</Typography>
          <Row k="액션" v={<b>{data.request.action}</b>} />
          <Row k="상태" v={data.request.status} />
          <Row k="요청 시각" v={new Date(data.request.requestedAt).toLocaleString('ko-KR')} />
          {data.request.revertReason && <Row k="revert 사유" v={<span style={{ color: '#c62828' }}>{data.request.revertReason}</span>} />}
          <Row k="파라미터" v={<code style={{ fontSize: 12 }}>{JSON.stringify(data.request.params)}</code>} />
        </Paper>
      )}

      <Paper sx={{ p: 3 }}>
        <Typography variant="h2" mb={2}>이벤트 ({data.events.length})</Typography>
        {data.events.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            적재된 이벤트가 없습니다(인덱싱 대상이 아니거나 아직 적재 전).
          </Typography>
        )}
        {data.events.map((e) => (
          <Box key={e.logIndex} sx={{ py: 1, borderBottom: '1px solid #eef1f6' }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip size="small" label={e.name} sx={{ fontWeight: 700 }} />
              <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{shortAddr(e.contract)}</Typography>
            </Stack>
            <Typography variant="caption" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
              {JSON.stringify(e.params)}
            </Typography>
          </Box>
        ))}
      </Paper>
    </Stack>
  );
}

function AddressView({ addr, onTx }: { addr: string; onTx: (h: string) => void }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['explorer-addr', addr],
    queryFn: () => api.get<ExplorerAddress>(`/explorer/address/${addr}`),
  });
  if (isLoading) return <LinearProgress />;
  if (error) return <Alert severity="warning">{error instanceof ApiError ? error.message : '조회 실패'}</Alert>;
  if (!data) return null;

  return (
    <Stack spacing={3}>
      <Paper sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" spacing={2} mb={2}>
          <Typography variant="h2">주소</Typography>
          {data.contract && <Chip size="small" color="info" label={`컨트랙트 · ${data.contract.symbol}`} sx={{ fontWeight: 700 }} />}
          {data.verified === true && <Chip size="small" color="success" label="✓ 등록됨(isVerified)" sx={{ fontWeight: 700 }} />}
          {data.verified === false && <Chip size="small" color="warning" label="미등록" sx={{ fontWeight: 700 }} />}
          {data.verified === null && <Chip size="small" label="확인 불가" sx={{ fontWeight: 700 }} />}
        </Stack>
        <Box mb={2}><AddressWithCopy address={data.address} /></Box>
        <Row k="네이티브 잔액" v={data.nativeBalance ? `${data.nativeBalance} META` : '조회 불가'} />
        <Row k="보낸 트랜잭션 수" v={data.txCount !== null ? String(data.txCount) : '조회 불가'} />
        {data.account && (
          <>
            <Row k="계정" v={<b>{data.account.loginId}</b>} />
            <Row k="키" v={`${data.account.keyType} · ${data.account.keyStatus}`} />
          </>
        )}
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h2" mb={2}>토큰 보유</Typography>
        {data.balances.length === 0 && <Typography variant="body2" color="text.secondary">보유 토큰이 없습니다.</Typography>}
        {data.balances.length > 0 && (
          <Table size="small">
            <TableHead><TableRow><TableCell>심볼</TableCell><TableCell>종류</TableCell><TableCell align="right">잔액</TableCell></TableRow></TableHead>
            <TableBody>
              {data.balances.map((b) => (
                <TableRow key={b.contract}>
                  <TableCell><b>{b.symbol}</b></TableCell>
                  <TableCell>{b.kind === 'SC' ? '스테이블코인' : '토큰증권'}</TableCell>
                  <TableCell align="right">{b.balance}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h2" mb={2}>관련 이벤트 ({data.events.length})</Typography>
        <Table size="small">
          <TableHead>
            <TableRow><TableCell>블록</TableCell><TableCell>이벤트</TableCell><TableCell>내용</TableCell><TableCell>트랜잭션</TableCell></TableRow>
          </TableHead>
          <TableBody>
            {data.events.map((e) => (
              <TableRow key={`${e.txHash}-${e.logIndex}`} hover>
                <TableCell>{Number(e.blockNumber).toLocaleString()}</TableCell>
                <TableCell><Chip size="small" label={e.name} sx={{ fontWeight: 700 }} /></TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 11, maxWidth: 340, wordBreak: 'break-all' }}>
                  {JSON.stringify(e.params)}
                </TableCell>
                <TableCell>
                  <Button size="small" onClick={() => onTx(e.txHash)} sx={{ fontFamily: 'monospace' }}>
                    {shortAddr(e.txHash)}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {data.events.length === 0 && (
              <TableRow><TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>관련 이벤트가 없습니다.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </Stack>
  );
}

/** 블록 상세 — `num` 은 블록 번호 또는 블록 해시다(서버가 둘 다 받는다) */
function BlockView({ num, onTx }: { num: string; onTx: (h: string) => void }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['explorer-block', num],
    queryFn: () => api.get<ExplorerBlock>(`/explorer/block/${num}`),
  });
  if (isLoading) return <LinearProgress />;
  if (error) return <Alert severity="warning">{error instanceof ApiError ? error.message : '조회 실패'}</Alert>;
  if (!data) return null;

  return (
    <Stack spacing={3}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h2" mb={2}>블록 #{data.number.toLocaleString()}</Typography>
        <Row k="해시" v={<code>{data.hash}</code>} />
        <Row k="이전 블록" v={<code>{data.parentHash}</code>} />
        <Row k="시각" v={new Date(data.timestampIso).toLocaleString('ko-KR')} />
        <Row k="생성 노드" v={<code>{data.miner}</code>} />
        <Row k="가스 사용 / 한도" v={`${Number(data.gasUsed).toLocaleString()} / ${Number(data.gasLimit).toLocaleString()}`} />
        <Row k="baseFee" v={data.baseFeePerGas ?? '—'} />
        <Row k="트랜잭션 수" v={String(data.txCount)} />
      </Paper>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h2" mb={2}>트랜잭션</Typography>
        {data.transactions.length === 0 && <Typography variant="body2" color="text.secondary">빈 블록입니다.</Typography>}
        <Stack>
          {data.transactions.map((h) => (
            <Button key={h} size="small" onClick={() => onTx(h)} sx={{ justifyContent: 'flex-start', fontFamily: 'monospace' }}>
              {h}
            </Button>
          ))}
        </Stack>
      </Paper>
    </Stack>
  );
}

function LinkAddr({ addr, onClick }: { addr: string; onClick: (a: string) => void }) {
  return (
    <Button size="small" onClick={() => onClick(addr)} sx={{ fontFamily: 'monospace', p: 0, minWidth: 0 }}>
      {addr}
    </Button>
  );
}
